import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon, 
  SparklesIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import HybridWhiteboard from '../components/HybridWhiteboard';

const WhiteboardPage = ({ user }) => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  
  // Core state
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWhiteboardData();
  }, [boardId]);

  const loadWhiteboardData = async () => {
    try {
      setLoading(true);
      
      // Load board details
      const boardResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/boards/${boardId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (boardResponse.ok) {
        const boardData = await boardResponse.json();
        setBoard(boardData.board);
      }
    } catch (error) {
      console.error('Error loading whiteboard data:', error);
      toast.error('Failed to load whiteboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading hybrid whiteboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navigation Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/boards')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {board?.name || 'Hybrid Whiteboard'}
              </h1>
              <p className="text-sm text-gray-500">
                Switch between Internal Canvas and Miro Professional
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-100 to-blue-100 text-gray-700 rounded-full text-sm font-medium">
              <SparklesIcon className="w-4 h-4" />
              <span>Hybrid Pro</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Hybrid Whiteboard */}
      <div className="flex-1">
        <HybridWhiteboard
          boardId={boardId}
          user={user}
          board={board}
          onContentCreate={(content) => {
            toast.success('Content created successfully');
          }}
        />
      </div>
    </div>
  );
};

export default WhiteboardPage; 